import * as React from 'react';
import { getEndOfWeek, getStartOfWeek } from '@mui/x-scheduler-internals/internals';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const DAY_AND_HOUR_DAYS = 4;
const formatYear = (adapter, date) => String(adapter.getYear(date));
const formatMonth3Letters = (adapter, date) => adapter.format(date, 'month3Letters');
const formatWeekday1Letter = (adapter, date) => adapter.format(date, 'weekday1Letter');
function formatWeekDayMonthAndDayOfMonth(adapter, date) {
  const f = adapter.formats;
  return adapter.formatByString(date, `${f.weekday3Letters}, ${f.month3Letters} ${f.dayOfMonth}`);
}
function formatMonthAndYear(adapter, date) {
  const f = adapter.formats;
  return adapter.formatByString(date, `${f.monthFullLetter} ${f.yearPadded}`);
}
function formatHourLabel(adapter, date, ampm) {
  const f = adapter.formats;
  const pattern = ampm ? `${f.hours12h}:${f.minutesPadded} ${f.meridiem}` : `${f.hours24h}:${f.minutesPadded}`;
  return adapter.formatByString(date, pattern);
}
export const EVENT_TIMELINE_PREMIUM_PRESET_CONFIGS = {
  dayAndHour: {
    timeResolution: 'hour',
    tickWidth: 64,
    headers: [{
      unit: 'day',
      renderCell: ({
        adapter,
        start
      }) => formatWeekDayMonthAndDayOfMonth(adapter, start)
    }, {
      unit: 'hour',
      renderCell: ({
        adapter,
        date,
        ampm
      }) => formatHourLabel(adapter, date, ampm)
    }],
    unitCount: DAY_AND_HOUR_DAYS,
    getStartDate: (adapter, visibleDate) => adapter.startOfDay(visibleDate),
    getEndDate: (adapter, start, unitCount) => adapter.endOfDay(adapter.addDays(start, unitCount - 1)),
    // `unitCount` is in days (the navigation step), but the grid ticks in hours. Pin
    // the CSS tick count to `4 × 24` so the grid width stays stable across DST and
    // matches the 24 hour cells `iterate()` emits per day.
    getCssUnitCount: () => DAY_AND_HOUR_DAYS * 24,
    navigate: (adapter, date, amount) => adapter.addDays(date, amount)
  },
  dayAndMonth: {
    timeResolution: 'day',
    tickWidth: 120,
    headers: [{
      unit: 'month',
      formatDate: formatMonthAndYear
    }, {
      unit: 'day',
      renderCell: ({
        adapter,
        date
      }) => /*#__PURE__*/_jsxs("span", {
        "data-slot": "dayCell",
        children: [/*#__PURE__*/_jsx("span", {
          "data-slot": "weekday",
          children: adapter.format(date, 'weekday1Letter')
        }), /*#__PURE__*/_jsx("span", {
          "data-slot": "dayOfMonth",
          children: adapter.format(date, 'dayOfMonth')
        })]
      })
    }],
    unitCount: 8 * 7,
    // 8 weeks
    getStartDate: (adapter, visibleDate) => adapter.startOfDay(visibleDate),
    getEndDate: (adapter, start, unitCount) => adapter.endOfDay(adapter.addDays(start, unitCount - 1)),
    navigate: (adapter, date, amount) => adapter.addDays(date, amount)
  },
  dayAndWeek: {
    timeResolution: 'day',
    tickWidth: 64,
    headers: [{
      unit: 'week',
      renderCell: ({
        adapter,
        start,
        end
      }) => `${formatWeekDayMonthAndDayOfMonth(adapter, start)} - ${formatWeekDayMonthAndDayOfMonth(adapter, adapter.addDays(end, -1))}`
    }, {
      unit: 'day',
      formatDate: formatWeekday1Letter
    }],
    unitCount: 16,
    // 16 weeks
    getStartDate: (adapter, visibleDate, weekStartsOn) => getStartOfWeek(adapter, visibleDate, weekStartsOn),
    getEndDate: (adapter, start, unitCount, weekStartsOn) => getEndOfWeek(adapter, adapter.addWeeks(start, unitCount - 1), weekStartsOn),
    getCssUnitCount: (adapter, start, end) => adapter.differenceInDays(end, start) + 1,
    navigate: (adapter, date, amount) => adapter.addWeeks(date, amount)
  },
  monthAndYear: {
    timeResolution: 'day',
    tickWidth: 6,
    headers: [{
      unit: 'year',
      formatDate: formatYear
    }, {
      unit: 'month',
      formatDate: formatMonth3Letters
    }],
    unitCount: 3 * 12,
    // 3 years
    getStartDate: (adapter, visibleDate) => adapter.startOfMonth(visibleDate),
    getEndDate: (adapter, start, unitCount) => adapter.endOfMonth(adapter.addMonths(start, unitCount - 1)),
    getCssUnitCount: (adapter, start, end) => adapter.differenceInDays(end, start) + 1,
    navigate: (adapter, date, amount) => adapter.addMonths(date, amount)
  },
  year: {
    timeResolution: 'year',
    tickWidth: 200,
    headers: [{
      unit: 'year',
      formatDate: formatYear
    }],
    unitCount: 30,
    // 30 years
    getStartDate: (adapter, visibleDate) => adapter.startOfYear(visibleDate),
    getEndDate: (adapter, start, unitCount) => adapter.endOfYear(adapter.addYears(start, unitCount - 1)),
    navigate: (adapter, date, amount) => adapter.addYears(date, amount)
  }
};

// Approximate number of ticks per day for each supported header unit; used to normalize
// `tickWidth` (px per tick) into a single "px per day" number that represents zoom level.
const TICKS_PER_DAY = {
  hour: 24,
  day: 1,
  week: 1 / 7,
  month: 1 / 30,
  year: 1 / 365
};

/**
 * Returns how many CSS pixels the preset spends representing one calendar day.
 * Higher = more zoomed in. Used to derive the canonical zoom ordering of presets.
 */
export function getPresetPxPerDay(preset) {
  const {
    timeResolution,
    tickWidth
  } = EVENT_TIMELINE_PREMIUM_PRESET_CONFIGS[preset];
  return tickWidth * TICKS_PER_DAY[timeResolution];
}